import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api-client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreateVisitModal } from "@/components/visits/CreateVisitModal";
import { formatAbsolute, parseBackendDate } from "@/lib/format";
import { Plus, CheckCircle2, XCircle, AlertCircle, Loader2, List, CalendarDays, ChevronLeft, ChevronRight, Bot, User as UserIcon, MailCheck, MailX, Users } from "lucide-react";
import { VISIT_STATUSES, type VisitStatus, type VisitType } from "@ai-leasing-os/shared";
import { cn } from "@/lib/cn";
import { EmptyState } from "@/components/ui/empty-state";
import { useToast } from "@/lib/toast";

type ViewMode = "list" | "calendar";

interface VisitRow {
  id: string;
  contact_id: string;
  property_id: string;
  conversation_id: string | null;
  assigned_user: string | null;
  onsite_contact_name: string | null;
  onsite_contact_phone: string | null;
  scheduled_start: string;
  scheduled_end: string | null;
  party_size: number | null;
  visit_type: VisitType;
  status: VisitStatus;
  notes: string | null;
  confirmation_email: string | null;
  confirmation_sent_at: string | null;
  /** "ai" | "human" | "system" | "api" - who created the row. */
  source: string;
  created_at: string;
  contact_display_name?: string | null;
  property_name?: string | null;
}

type SourceFilter = "all" | "ai" | "human";

export function VisitsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState<VisitStatus | null>("scheduled");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useState<ViewMode>("list");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["visits", statusFilter, view],
    queryFn: () => {
      const params = new URLSearchParams({ limit: view === "calendar" ? "500" : "100" });
      // In calendar view we want all statuses on the month - let the user
      // narrow with the chips if they really want.
      if (view === "list" && statusFilter) params.set("status", statusFilter);
      return api.get<VisitRow[]>(`/api/visits?${params.toString()}`);
    },
    refetchInterval: 30_000,
  });

  // Source filtering is client-side: the row set is already capped (100 list
  // / 500 calendar) and `source` rarely needs a server round-trip to narrow.
  // "human" groups every non-AI origin (manual, api, system) since from the
  // operator's view "the bot booked it" vs "a person booked it" is the split
  // that matters.
  const visibleRows = rows.filter((v) => {
    if (sourceFilter === "all") return true;
    if (sourceFilter === "ai") return v.source === "ai";
    return v.source !== "ai";
  });
  const aiCount = rows.filter((v) => v.source === "ai").length;

  const updateMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: VisitStatus }) =>
      api.put(`/api/visits/${id}`, { status }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["visits"] });
      const label =
        vars.status === "completed" ? "Marked completed" :
        vars.status === "cancelled" ? "Visit cancelled" :
        vars.status === "no_show" ? "Marked as no-show" :
        "Visit updated";
      toast.success(label);
    },
    onError: (e) =>
      toast.error("Could not update visit", e instanceof Error ? e.message : undefined),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Visits</h1>
          <p className="text-sm text-slate-500">Property viewings and tours.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
            <button
              onClick={() => setView("list")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition",
                view === "list" ? "bg-slate-900 text-white" : "bg-brand-surface text-slate-700 hover:bg-slate-50",
              )}
            >
              <List className="h-3.5 w-3.5" /> List
            </button>
            <button
              onClick={() => setView("calendar")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition border-l border-slate-300",
                view === "calendar" ? "bg-slate-900 text-white" : "bg-brand-surface text-slate-700 hover:bg-slate-50",
              )}
            >
              <CalendarDays className="h-3.5 w-3.5" /> Calendar
            </button>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> New visit
          </Button>
        </div>
      </header>

      {/* Source filter — applies to both list and calendar. "AI booked"
          surfaces what the bot scheduled on its own. */}
      <Card className="p-3 flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">
          booked by
        </span>
        {([
          { key: "all", label: "everyone" },
          { key: "ai", label: "AI" },
          { key: "human", label: "human" },
        ] as { key: SourceFilter; label: string }[]).map((opt) => (
          <button
            key={opt.key}
            onClick={() => setSourceFilter(opt.key)}
            className={cn(
              "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium",
              sourceFilter === opt.key
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200",
            )}
          >
            {opt.key === "ai" && <Bot className="h-3 w-3" />}
            {opt.key === "human" && <UserIcon className="h-3 w-3" />}
            {opt.label}
          </button>
        ))}
        {aiCount > 0 && (
          <span className="text-[11px] text-brand-primary">
            {aiCount} booked by the bot
          </span>
        )}
        <span className="ml-auto text-xs text-slate-500">
          {visibleRows.length} shown
        </span>
      </Card>

      {view === "calendar" ? (
        <VisitCalendar
          visits={visibleRows}
          onMarkCompleted={(id) => updateMut.mutate({ id, status: "completed" })}
          onCancel={(id) => updateMut.mutate({ id, status: "cancelled" })}
        />
      ) : (
      <>
      <Card className="p-3 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setStatusFilter(null)}
          className={cn("px-2 py-0.5 rounded text-[11px] font-medium",
            statusFilter === null ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
          )}
        >
          all
        </button>
        {VISIT_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s === statusFilter ? null : s)}
            className={cn("px-2 py-0.5 rounded text-[11px] font-medium",
              statusFilter === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
            )}
          >
            {s}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500">{visibleRows.length} visits</span>
      </Card>

      <Card>
        {isLoading ? (
          <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
        ) : visibleRows.length === 0 ? (
          sourceFilter === "ai" ? (
            <EmptyState
              icon={<Bot className="h-6 w-6" />}
              title="The bot hasn't booked anything yet"
              description="When the assistant confirms a visit or booking with a lead, it shows up here automatically."
              action={{ label: "Show all bookings", onClick: () => setSourceFilter("all") }}
            />
          ) : (
            <EmptyState
              icon={<CalendarDays className="h-6 w-6" />}
              title="No visits scheduled"
              description="Bookings the assistant confirms appear here, or add one yourself."
              action={{
                label: "New visit",
                onClick: () => setCreateOpen(true),
                icon: <Plus className="h-4 w-4" />,
              }}
            />
          )
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Scheduled</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Property</th>
                <th className="px-3 py-2 text-left">Contact</th>
                <th className="px-3 py-2 text-left">Party</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((v) => (
                <tr key={v.id} className="border-b border-slate-100">
                  <td className="px-3 py-2"><StatusPill status={v.status} /></td>
                  <td className="px-3 py-2 tabular-nums text-slate-700">
                    {formatAbsolute(v.scheduled_start)}
                  </td>
                  <td className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500">{v.visit_type}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {v.property_name ?? (
                      <span className="font-mono text-xs text-slate-500">{v.property_id.slice(-8)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {v.contact_display_name ?? (
                      <span className="font-mono text-xs text-slate-500">{v.contact_id.slice(-8)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {v.party_size ? (
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3 text-slate-400" />
                        {v.party_size}
                      </span>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2"><SourcePill source={v.source} /></td>
                  <td className="px-3 py-2 text-xs">
                    <EmailIndicator
                      email={v.confirmation_email}
                      sentAt={v.confirmation_sent_at}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {v.status === "scheduled" && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateMut.mutate({ id: v.id, status: "completed" })}
                          title="Mark completed"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateMut.mutate({ id: v.id, status: "cancelled" })}
                          title="Cancel"
                        >
                          <XCircle className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateMut.mutate({ id: v.id, status: "no_show" })}
                          title="No-show"
                        >
                          <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      </>
      )}

      <CreateVisitModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["visits"] });
          setCreateOpen(false);
          toast.success("Visit created");
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// VisitCalendar - monthly grid view.
// ---------------------------------------------------------------------------

function VisitCalendar({
  visits,
  onMarkCompleted,
  onCancel,
}: {
  visits: VisitRow[];
  onMarkCompleted: (id: string) => void;
  onCancel: (id: string) => void;
}) {
  // The currently-displayed month (first day at 00:00 local).
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  // Build a 6×7 grid starting on Monday.
  const firstWeekday = (cursor.getDay() + 6) % 7; // 0 = Monday
  const start = new Date(cursor);
  start.setDate(1 - firstWeekday);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }

  // Group visits by yyyy-mm-dd.
  const byDay = new Map<string, VisitRow[]>();
  for (const v of visits) {
    const key = dayKey(parseBackendDate(v.scheduled_start));
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key)!.push(v);
  }

  const today = dayKey(new Date());
  const monthIdx = cursor.getMonth();

  return (
    <Card>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => shiftMonth(setCursor, -1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => {
            const d = new Date();
            setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
          }}>
            Today
          </Button>
          <Button size="sm" variant="ghost" onClick={() => shiftMonth(setCursor, 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <h3 className="text-sm font-semibold text-slate-900">{monthLabel}</h3>
        <span className="text-[11px] text-slate-500">{visits.length} visits loaded</span>
      </div>

      <div className="grid grid-cols-7 border-b border-slate-200">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 text-center">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 grid-rows-6 border-l border-t border-slate-200">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === monthIdx;
          const key = dayKey(d);
          const dayVisits = byDay.get(key) ?? [];
          const isToday = key === today;
          return (
            <div
              key={i}
              className={cn(
                "min-h-[96px] border-r border-b border-slate-200 p-1.5 text-[11px] relative",
                !inMonth && "bg-slate-50/50",
                isToday && "bg-blue-50/40",
              )}
            >
              <div className={cn(
                "text-[11px] font-medium mb-1 flex items-center justify-between",
                inMonth ? "text-slate-700" : "text-slate-400",
                isToday && "text-blue-700",
              )}>
                <span>{d.getDate()}</span>
                {dayVisits.length > 2 && (
                  <span className="text-[9px] text-slate-400">{dayVisits.length}</span>
                )}
              </div>
              <div className="space-y-0.5">
                {dayVisits.slice(0, 3).map((v) => (
                  <VisitChip
                    key={v.id}
                    visit={v}
                    onMarkCompleted={onMarkCompleted}
                    onCancel={onCancel}
                  />
                ))}
                {dayVisits.length > 3 && (
                  <div className="text-[10px] text-slate-500 px-1">
                    +{dayVisits.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function VisitChip({
  visit, onMarkCompleted, onCancel,
}: {
  visit: VisitRow;
  onMarkCompleted: (id: string) => void;
  onCancel: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const t = parseBackendDate(visit.scheduled_start);
  const time = t.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const colors: Record<VisitStatus, string> = {
    scheduled: "bg-blue-100 text-blue-900 hover:bg-blue-200",
    completed: "bg-green-100 text-green-900",
    cancelled: "bg-slate-100 text-slate-500 line-through",
    no_show:   "bg-amber-100 text-amber-900",
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full text-left rounded px-1.5 py-0.5 text-[10px] truncate transition-colors",
          colors[visit.status],
        )}
        title={`${time} · ${visit.visit_type}`}
      >
        <span className="font-mono tabular-nums">{time}</span> · {visit.visit_type}
      </button>
      {open && (
        <div className="absolute z-10 left-0 top-full mt-1 w-60 rounded-md border border-slate-200 bg-brand-surface shadow-lg p-2 text-[11px] space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-900">{time}</div>
            <SourcePill source={visit.source} />
          </div>
          <div className="text-slate-700">Type: {visit.visit_type}</div>
          <div className="text-slate-700">Status: {visit.status}</div>
          {visit.property_name && (
            <div className="text-slate-700">Property: {visit.property_name}</div>
          )}
          {visit.contact_display_name && (
            <div className="text-slate-700">Lead: {visit.contact_display_name}</div>
          )}
          {visit.party_size != null && visit.party_size > 0 && (
            <div className="text-slate-700 inline-flex items-center gap-1">
              <Users className="h-3 w-3 text-slate-400" /> Party of {visit.party_size}
            </div>
          )}
          {visit.onsite_contact_name && (
            <div className="text-slate-700">Onsite: {visit.onsite_contact_name}</div>
          )}
          {(visit.confirmation_email || visit.confirmation_sent_at) && (
            <div>
              <EmailIndicator
                email={visit.confirmation_email}
                sentAt={visit.confirmation_sent_at}
              />
            </div>
          )}
          {visit.notes && <div className="text-slate-600 italic">{visit.notes}</div>}
          {visit.status === "scheduled" && (
            <div className="flex gap-1 pt-1 border-t border-slate-100">
              <Button size="sm" variant="outline" onClick={() => { onMarkCompleted(visit.id); setOpen(false); }}>
                <CheckCircle2 className="h-3 w-3" /> Done
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { onCancel(visit.id); setOpen(false); }}>
                <XCircle className="h-3 w-3 text-red-500" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function shiftMonth(
  set: React.Dispatch<React.SetStateAction<Date>>,
  delta: number,
) {
  set((prev) => {
    const next = new Date(prev);
    next.setMonth(next.getMonth() + delta);
    next.setDate(1);
    return next;
  });
}

function StatusPill({ status }: { status: VisitStatus }) {
  const map: Record<VisitStatus, { variant: "info" | "success" | "danger" | "warning"; label: string }> = {
    scheduled: { variant: "info", label: "scheduled" },
    completed: { variant: "success", label: "completed" },
    cancelled: { variant: "danger", label: "cancelled" },
    no_show: { variant: "warning", label: "no-show" },
  };
  const m = map[status];
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

/**
 * Compact source indicator. AI-created bookings get the distinctive brand
 * accent so operators can spot the bot's work at a glance. Human / API
 * sources fall back to a neutral pill.
 */
function SourcePill({ source }: { source: string }) {
  if (source === "ai") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-brand-soft px-1.5 py-0.5 text-[10px] font-medium text-brand-primary">
        <Bot className="h-3 w-3" /> AI
      </span>
    );
  }
  if (source === "human" || !source) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
        <UserIcon className="h-3 w-3" /> human
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
      {source}
    </span>
  );
}

/**
 * Tri-state email confirmation indicator.
 *
 * - sent ok → green check + the address on hover
 * - email present but no sent_at → red cross (transport failed or skipped)
 * - no email at all → muted dash
 */
function EmailIndicator({
  email,
  sentAt,
}: {
  email: string | null;
  sentAt: string | null;
}) {
  if (sentAt && email) {
    return (
      <span
        className="inline-flex items-center gap-1 text-emerald-700"
        title={`Sent to ${email}`}
      >
        <MailCheck className="h-3.5 w-3.5" />
        <span className="hidden md:inline truncate max-w-[140px]">{email}</span>
      </span>
    );
  }
  if (email && !sentAt) {
    return (
      <span
        className="inline-flex items-center gap-1 text-red-600"
        title={`Email on file (${email}) but no confirmation was sent`}
      >
        <MailX className="h-3.5 w-3.5" />
        <span className="hidden md:inline truncate max-w-[140px]">{email}</span>
      </span>
    );
  }
  return <span className="text-slate-300">-</span>;
}
